/**
 * EPN-42 correction pass — the approved Admin Dashboard's own Product Offerings & Other Services set,
 * reconciled directly against `escochex-admin-overview-dashboard-v2-final.html` (exact names/order/
 * descriptions below). Deliberately a LOCAL, Admin-Dashboard-only component/config, NOT a rewrite of the
 * shared `dashboard-v2/types.ts` PRODUCT_OFFERINGS constant — that constant is also the standalone
 * `/dashboard`'s own content and changing it would silently alter that page's approved composition, which
 * is out of this ticket's scope.
 *
 * "Learn More" only navigates where a real, already-verified destination exists. Today that is exactly one
 * card: "Benefits Administration" has the identical title/description as the shared PRODUCT_OFFERINGS
 * entry of the same name, so its already-real `learnMoreUrl` is reused as-is. The final HTML's own JS
 * doesn't define real destinations for any card (it opens a generic preview modal) and none of the other
 * four approved titles correspond to an existing entry closely enough to justify reusing its URL, so their
 * "Learn More" renders as an honest, visibly non-interactive control instead of an invented URL or a
 * decorative control that looks clickable but silently does nothing.
 */
interface AdminProductOffering {
  title: string;
  description: string;
  icon: string;
  /** Font Awesome 4.7 class name, used when no existing real icon asset corresponds to this offering. */
  iconAsset?: string;
  learnMoreUrl?: string;
}

const ADMIN_PRODUCT_OFFERINGS: AdminProductOffering[] = [
  { title: "Worker's Comp Audit", description: "Review worker's comp setup and classification needs.", icon: 'fa-shield' },
  { title: 'HR Services', description: 'Get support for HR compliance and employee management', icon: 'fa-id-badge' },
  { title: 'Insurance', description: 'Review business and employee insurance options.', icon: 'fa-home' },
  {
    title: 'Benefits Administration', description: 'Manage or explore employee benefits support.', icon: 'fa-umbrella',
    iconAsset: '/img/offerings/Emp.svg', learnMoreUrl: 'https://www.escochecks.com/service-benefits-administration',
  },
  { title: '401(k)', description: 'Explore retirement plan options for your employees.', icon: 'fa-percent' },
];

export function AdminProductOfferingsRow() {
  return (
    <section className="ed-section-card">
      <div className="ed-section-header ed-offerings-header">
        <div className="ed-section-header-left">
          <span className="ed-section-icon ed-offerings-icon" aria-hidden="true">★</span>
          <h2 className="ed-section-title ed-offerings-title">Product Offerings & Other Services</h2>
        </div>
      </div>
      <div className="ed-offerings-scroll">
        {ADMIN_PRODUCT_OFFERINGS.map((offering) => (
          <article key={offering.title} className="ed-offering-card">
            <div className="ed-offering-card-icon">
              {offering.iconAsset ? <img src={offering.iconAsset} alt="" /> : <i className={`fa ${offering.icon}`} aria-hidden="true" />}
            </div>
            <h3 className="ed-offering-card-title">{offering.title}</h3>
            <p className="ed-offering-card-desc">{offering.description}</p>
            {offering.learnMoreUrl ? (
              <a href={offering.learnMoreUrl} target="_blank" rel="noopener noreferrer" className="ed-offering-learn-more">
                Learn More <span aria-hidden="true">↗</span>
              </a>
            ) : (
              <span className="ed-offering-learn-more ed-offering-learn-more--disabled" aria-disabled="true">
                Learn More
              </span>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
